/**
 * Rascunho do agendamento em andamento.
 *
 * A etapa "Quer criar sua conta?" tira o cliente da página (vai para
 * `/cliente/cadastro` ou `/cliente/login`). Sem rascunho ele voltaria com a
 * seleção zerada — exatamente o retrabalho que a conta deveria evitar.
 */

import type { BookingFormData } from "../types";
import type { BookingWizardStepId } from "./bookingWizard";

const DRAFT_STORAGE_PREFIX = "booking-wizard-draft";
/** Rascunho velho não vale nada: o horário provavelmente já foi tomado. */
const DRAFT_MAX_AGE_MS = 30 * 60 * 1000;

export interface BookingWizardDraft {
  slug: string;
  savedAt: string;
  stepId: BookingWizardStepId;
  selectedServiceId: string;
  selectedServiceIds: string[];
  selectedProviderId: string;
  date: string;
  selectedTime: string;
  formData: Partial<BookingFormData>;
  customValues: Record<string, unknown>;
}

export function buildBookingDraftStorageKey(slug: string | null | undefined): string {
  return `${DRAFT_STORAGE_PREFIX}:${String(slug || "").trim()}`;
}

export function isBookingDraftFresh(
  draft: BookingWizardDraft,
  now: number = Date.now()
): boolean {
  const savedAt = Date.parse(String(draft.savedAt || ""));
  if (!Number.isFinite(savedAt)) return false;
  const age = now - savedAt;
  return age >= 0 && age <= DRAFT_MAX_AGE_MS;
}

export function parseBookingDraft(
  raw: string | null,
  slug: string | null | undefined,
  now: number = Date.now()
): BookingWizardDraft | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<BookingWizardDraft>;
    if (!parsed || typeof parsed !== "object") return null;
    if (String(parsed.slug || "") !== String(slug || "")) return null;

    const draft: BookingWizardDraft = {
      slug: String(parsed.slug || ""),
      savedAt: String(parsed.savedAt || ""),
      stepId: (parsed.stepId as BookingWizardStepId) || "service",
      selectedServiceId: String(parsed.selectedServiceId || ""),
      selectedServiceIds: Array.isArray(parsed.selectedServiceIds)
        ? parsed.selectedServiceIds.map((id) => String(id)).filter(Boolean)
        : [],
      selectedProviderId: String(parsed.selectedProviderId || ""),
      date: String(parsed.date || ""),
      selectedTime: String(parsed.selectedTime || ""),
      formData:
        parsed.formData && typeof parsed.formData === "object" ? parsed.formData : {},
      customValues:
        parsed.customValues && typeof parsed.customValues === "object"
          ? (parsed.customValues as Record<string, unknown>)
          : {},
    };

    return isBookingDraftFresh(draft, now) ? draft : null;
  } catch {
    return null;
  }
}

export function saveBookingDraft(draft: BookingWizardDraft): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      buildBookingDraftStorageKey(draft.slug),
      JSON.stringify(draft)
    );
  } catch {
    // sessionStorage indisponível (modo privado, quota): seguir sem rascunho.
  }
}

export function loadBookingDraft(
  slug: string | null | undefined
): BookingWizardDraft | null {
  if (typeof window === "undefined") return null;
  try {
    return parseBookingDraft(
      sessionStorage.getItem(buildBookingDraftStorageKey(slug)),
      slug
    );
  } catch {
    return null;
  }
}

export function clearBookingDraft(slug: string | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(buildBookingDraftStorageKey(slug));
  } catch {
    // Nada a fazer: o rascunho expira sozinho em 30 minutos.
  }
}

/** Campos que vêm da conta do cliente e ficam travados quando ele está logado. */
export const CUSTOMER_IDENTITY_FIELDS = ["name", "email", "whatsapp"] as const;

export type CustomerIdentityField = (typeof CUSTOMER_IDENTITY_FIELDS)[number];

/**
 * MBR-14: campos que a conta já tem e por isso ficam travados. O que falta
 * (conta criada no balcão sem WhatsApp, por exemplo) continua editável.
 */
export function getCustomerLockedFields(profile: {
  fullName?: string | null;
  email?: string | null;
  whatsapp?: string | null;
}): CustomerIdentityField[] {
  const locked: CustomerIdentityField[] = [];
  if (String(profile.fullName || "").trim()) locked.push("name");
  if (String(profile.email || "").trim()) locked.push("email");
  if (String(profile.whatsapp || "").replace(/\D/g, "").length >= 10) {
    locked.push("whatsapp");
  }
  return locked;
}

/**
 * Junta o `formData` do rascunho ao formulário atual sem apagar nada.
 *
 * O rascunho é salvo na etapa de conta, antes de "Seus dados", então costuma
 * trazer nome, e-mail e WhatsApp vazios. Se o perfil do cliente logado já
 * preencheu o formulário, um spread simples apagaria esses dados. Por isso:
 * valor vazio do rascunho é ignorado e, com cliente logado, os dados da conta
 * prevalecem.
 */
export function mergeBookingDraftFormData<T extends Partial<BookingFormData>>(
  current: T,
  draftFormData: Partial<BookingFormData>,
  options: {
    preserveCustomerIdentity?: boolean;
    /**
     * Só estes campos ficam com o valor atual. Com perfil incompleto, o que a
     * conta não tem (o WhatsApp digitado antes de entrar) vem do rascunho.
     */
    preserveFields?: readonly CustomerIdentityField[];
  } = {}
): T {
  const next: Record<string, unknown> = { ...current };
  const preserved: readonly string[] =
    options.preserveFields ??
    (options.preserveCustomerIdentity ? CUSTOMER_IDENTITY_FIELDS : []);

  for (const [key, value] of Object.entries(draftFormData)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (preserved.includes(key)) continue;
    next[key] = value;
  }

  return next as T;
}
